import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Warning,
  Error,
  Info,
  Notifications,
  Refresh,
  Visibility,
  Block,
  CheckCircle,
  Schedule,
} from '@mui/icons-material';
import { InactiveAccountAlert } from '../types';

const InactiveAccountAlerts: React.FC = () => {
  const [alerts, setAlerts] = useState<InactiveAccountAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [riskLevel, setRiskLevel] = useState<string>('all');

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setLoading(true);
        // Mock data - replace with actual API call
        const mockAlerts: InactiveAccountAlert[] = [
          {
            id: '1',
            accountId: 'ACC001',
            accountType: 'merchant',
            lastActivityDate: '2024-01-15T10:00:00Z',
            daysInactive: 45,
            riskLevel: 'high',
            alertType: 'critical',
            recommendedAction: 'Contact merchant immediately',
          },
          {
            id: '2',
            accountId: 'ACC002',
            accountType: 'loan',
            lastActivityDate: '2024-01-20T14:30:00Z',
            daysInactive: 38,
            riskLevel: 'medium',
            alertType: 'warning',
            recommendedAction: 'Send reminder notification',
          },
          {
            id: '3',
            accountId: 'ACC003',
            accountType: 'merchant',
            lastActivityDate: '2024-01-25T09:15:00Z',
            daysInactive: 32,
            riskLevel: 'low',
            alertType: 'warning',
            recommendedAction: 'Monitor for next 7 days',
          },
          {
            id: '4',
            accountId: 'ACC004',
            accountType: 'bank',
            lastActivityDate: '2024-01-10T16:45:00Z',
            daysInactive: 52,
            riskLevel: 'high',
            alertType: 'critical',
            recommendedAction: 'Account suspension review',
          },
        ];

        setAlerts(mockAlerts);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch alerts');
        setLoading(false);
      }
    };

    fetchAlerts();
  }, []);

  const filteredAlerts = alerts.filter(alert => {
    if (filterType !== 'all' && alert.accountType !== filterType) return false;
    if (riskLevel !== 'all' && alert.riskLevel !== riskLevel) return false;
    return true;
  });

  const criticalAlerts = filteredAlerts.filter(alert => alert.alertType === 'critical');
  const warningAlerts = filteredAlerts.filter(alert => alert.alertType === 'warning');

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getAlertIcon = (alertType: string) => {
    switch (alertType) {
      case 'critical': return <Error color="error" />;
      case 'warning': return <Warning color="warning" />;
      default: return <Info color="info" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Inactive Account Alerts
      </Typography>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Alerts
                </Typography>
                <Typography variant="h5" component="div">
                  {filteredAlerts.length}
                </Typography>
              </Box>
              <Badge badgeContent={filteredAlerts.length} color="primary">
                <Notifications color="primary" />
              </Badge>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Critical Alerts
                </Typography>
                <Typography variant="h5" component="div" color="error.main">
                  {criticalAlerts.length}
                </Typography>
              </Box>
              <Error color="error" />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Warning Alerts
                </Typography>
                <Typography variant="h5" component="div" color="warning.main">
                  {warningAlerts.length}
                </Typography>
              </Box>
              <Warning color="warning" />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Accounts &gt;30 Days
                </Typography>
                <Typography variant="h5" component="div" color="info.main">
                  {filteredAlerts.filter(a => a.daysInactive > 30).length}
                </Typography>
              </Box>
              <Schedule color="info" />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Account Type</InputLabel>
            <Select
              value={filterType}
              label="Account Type"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="merchant">Merchant</MenuItem>
              <MenuItem value="loan">Loan</MenuItem>
              <MenuItem value="bank">Bank</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Risk Level</InputLabel>
            <Select
              value={riskLevel}
              label="Risk Level"
              onChange={(e) => setRiskLevel(e.target.value)}
            >
              <MenuItem value="all">All Levels</MenuItem>
              <MenuItem value="high">High Risk</MenuItem>
              <MenuItem value="medium">Medium Risk</MenuItem>
              <MenuItem value="low">Low Risk</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {/* Handle refresh */}}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Alerts Table */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Inactive Account Alerts ({filteredAlerts.length})
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Account ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell>Days Inactive</TableCell>
                <TableCell>Risk Level</TableCell>
                <TableCell>Alert Type</TableCell>
                <TableCell>Recommended Action</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAlerts.map((alert) => (
                <TableRow key={alert.id}>
                  <TableCell>{alert.accountId}</TableCell>
                  <TableCell>
                    <Chip
                      label={alert.accountType}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(alert.lastActivityDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${alert.daysInactive} days`}
                      color={alert.daysInactive > 45 ? 'error' : alert.daysInactive > 30 ? 'warning' : 'info'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={alert.riskLevel}
                      color={getRiskColor(alert.riskLevel) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getAlertIcon(alert.alertType)}
                      <Chip
                        label={alert.alertType}
                        color={alert.alertType === 'critical' ? 'error' : 'warning'}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                      {alert.recommendedAction}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mark as Resolved">
                        <IconButton size="small" color="success">
                          <CheckCircle />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Suspend Account">
                        <IconButton size="small" color="error">
                          <Block />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Action Buttons */}
      <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
        <Button
          variant="contained"
          color="error"
          startIcon={<Error />}
          onClick={() => {/* Handle critical alerts */}}
        >
          Process Critical Alerts
        </Button>
        <Button
          variant="outlined"
          startIcon={<Warning />}
          onClick={() => {/* Handle warning alerts */}}
        >
          Process Warning Alerts
        </Button>
        <Button
          variant="outlined"
          startIcon={<Schedule />}
          onClick={() => {/* Handle scheduled actions */}}
        >
          Scheduled Actions
        </Button>
      </Box>
    </Box>
  );
};

export default InactiveAccountAlerts;
